import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';

// Import Three.js components
import { createScene, handleResize, cleanupScene } from './three/Scene.jsx';
import { createEarth, cleanupEarth } from './three/Earth.jsx';
import { createAirplane, updateAirplanePosition, cleanupAirplane } from './three/Airplane.jsx';
import { createLidar, updateLasers, cleanupLidar } from './three/Lidar.jsx';
import { createStars, cleanupStars } from './three/Stars.jsx';
import { setupControls, cleanupControls } from './three/Controls.jsx';
import { createLightPrism, updateLightBeam, cleanupLightPrism } from './three/LightPrism.jsx';
import { createLidarPointCloud, updateLidarPointCloud, cleanupLidarPointCloud } from './three/LidarPointCloud.jsx';
import { EARTH_ROTATION_SPEED, AIRPLANE } from './three/constants.js';

const Demo = forwardRef((props, ref) => {
  const mountRef = useRef(null);
  const sceneDataRef = useRef(null);
  const controlsRef = useRef(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [rotationSpeed, setRotationSpeed] = useState(EARTH_ROTATION_SPEED);
  
  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    scrollToContent: () => {
      // Zoom out or move camera to show the content section
      if (sceneDataRef.current && sceneDataRef.current.camera) {
        const camera = sceneDataRef.current.camera;
        
        // Move the camera down to view the content section
        // Animation can be improved later if needed
        camera.position.y += 5;
        
        // Ensure we're not too close
        if (camera.position.z < 15) {
          camera.position.z = 15;
        }
      }
    }
  }));

  useEffect(() => {
    // Create the scene, camera, and renderer
    const sceneData = createScene(mountRef);
    sceneDataRef.current = sceneData; // Store for later use
    const { scene, camera, renderer } = sceneData;
    
    // Create the Earth
    const earth = createEarth();
    const { earthGroup } = earth;
    scene.add(earthGroup);
    
    // Create the lidar point cloud first to get access to the group
    const lidarPointCloud = createLidarPointCloud();
    scene.add(lidarPointCloud.pointCloudGroup);
    
    // Add the ray group directly to the scene (not as child of point cloud)
    // This keeps the rays from rotating with the point cloud
    scene.add(lidarPointCloud.loadingState.rayGroup);
    
    // Setup mouse controls with point cloud group for rotation
    const controls = setupControls(sceneData, earthGroup, lidarPointCloud.pointCloudGroup);
    controlsRef.current = controls; // Store for later use
    
    // Create the airplane
    const airplane = createAirplane();
    const { airplaneGroup, planeBody, propellerGroup } = airplane;
    earthGroup.add(airplaneGroup);
    
    // Create the LiDAR system
    const lidar = createLidar(planeBody);
    const { laserGroup } = lidar;
    // Important: Add laserGroup to scene, not airplaneGroup, so lasers remain visible
    scene.add(laserGroup);
    
    // Create the Light Prism system
    const lightPrism = createLightPrism();
    scene.add(lightPrism.mainGroup);
    scene.add(lightPrism.particles);
    scene.add(lightPrism.connectingBeamParticles);
    
    // Point cloud already created earlier
    
    // Create stars
    const stars = createStars();
    scene.add(stars.stars);
    
    // Handle window resize
    const resizeHandler = () => handleResize({...sceneData, mountRef});
    window.addEventListener('resize', resizeHandler);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Only auto-rotate if enabled and not being dragged
      if (autoRotate && !controls.isDragging()) {
        earthGroup.rotation.y += rotationSpeed;
      }
      
      // Animate the propeller rotation
      propellerGroup.rotation.x += AIRPLANE.PROPELLER_ROTATION_SPEED;
      
      // Update airplane position and orientation
      updateAirplanePosition(airplaneGroup, planeBody);
      
      // No need to rotate LiDAR cone manually, it's handled in updateLasers now
      
      // Always update laser beams from LiDAR - creates scanning pattern
      updateLasers(airplaneGroup, lidar);
      
      // Check if point cloud is fully loaded
      const isPointCloudLoaded = lidarPointCloud.loadingState.loadedPoints >= lidarPointCloud.loadingState.totalPoints 
                              && lidarPointCloud.loadingState.totalPoints > 0;
    
      // Only show particle beams if point cloud is not fully loaded
      if (!isPointCloudLoaded) {
        // Update technicolor beam between airplane and light prism with reflections
        updateLightBeam(airplane, lightPrism, null, scene, renderer);
        
        // Ensure particle beams are visible
        lightPrism.particles.visible = true;
        lightPrism.connectingBeamParticles.visible = true;
      } else {
        // Hide particle beams when loading is complete
        lightPrism.particles.visible = false;
        lightPrism.connectingBeamParticles.visible = false;
      }
      
      // Update lidar point cloud loading animation
      updateLidarPointCloud(lidarPointCloud);
      
      renderer.render(scene, camera);
    };

    animate();

    // Cleanup function
    return () => {
      window.removeEventListener('resize', resizeHandler);
      
      // Remove controls
      cleanupControls(sceneData, controls);
      
      // Clean up all components
      cleanupLidar(scene, lidar);
      cleanupLightPrism(scene, lightPrism);
      cleanupLidarPointCloud(scene, lidarPointCloud);
      cleanupAirplane(scene, airplane);
      cleanupEarth(scene, earth);
      cleanupStars(scene, stars);
      cleanupScene(sceneData);
    };
  }, [autoRotate, rotationSpeed]);

  return <div className="canvas-container" ref={mountRef}></div>;
});

export default Demo;